import React, { FC, useMemo } from 'react';
import { getPatientDualRingPlacement, getPatientRingFramePx, useElementSize } from '../../../tools/patientDyopLayout';
import { PieChart } from 'react-minimal-pie-chart';
// types
import { dyopsType } from '../../../types/dyops';
// style
import './index.scss';
// localization
import strings from '../../../localization';
import { chromaticHexesForRender } from '../../../tools/chromaticContrast';
import { clampOvalMinorRatio, withDefaultOvalFields } from '../../../tools/ovalDefaults';
import {
  getInfantTargetPosition,
  getInfantTargetSides,
  resolveInfantLayoutProfile,
  withDefaultInfantFields,
} from '../../../tools/infantDefaults';
import type { InfantTargetSide } from '../../../tools/infantDefaults';

/** Draw ring at this multiple of size then scale down so oval squash + spin alias less on screen pixels. */
const DYOP_OVAL_SUPER_SCALE = 2;

interface DyopsProps {
  diametr: number;
  label?: boolean;
  active?: string;
  rotation?: string;
  dyops?: dyopsType;
  isUserConnected?: boolean;
  /** Single centered ring for modal preview (always spinning). */
  singleRingPreview?: boolean;
  /** Forces infant single-target layout even when infantMode flag is missing on dyops. */
  forceInfantLayout?: boolean;
  /** Horizontal gap between left and right eye rings on the doctor stage. */
  eyeSeparationPx?: number;
}

type RingChartProps = {
  diametr: number;
  spinClass: string;
  ellipseStyle: React.CSSProperties;
  scaleStyle: React.CSSProperties;
  pieData: Array<{ value: number; color: string }>;
  /** When true, rasterize the chart at higher resolution before oval squash to reduce motion aliasing. */
  ovalSupersample?: boolean;
};

/** Renders one Dyop ring with oval transforms on outer layers and optional high-DPI raster path for oval mode. */
const RingChart: FC<RingChartProps> = ({ diametr, spinClass, ellipseStyle, scaleStyle, pieData, ovalSupersample }) => {
  const outerPx = Math.round(diametr);
  const innerPx = Math.round(diametr * DYOP_OVAL_SUPER_SCALE);
  const downscale = 1 / DYOP_OVAL_SUPER_SCALE;

  // Explicit pixel box: PieChart uses width/height 100% and must not rely on absolute + min-size only.
  const spinStyle: React.CSSProperties = {
    position: 'relative',
    width: `${outerPx}px`,
    height: `${outerPx}px`,
    minWidth: `${outerPx}px`,
    minHeight: `${outerPx}px`,
    ...(ovalSupersample ? { overflow: 'hidden' as const } : {}),
  };

  const chartStyle: React.CSSProperties = {
    display: 'block',
    position: 'relative',
    width: `${outerPx}px`,
    height: `${outerPx}px`,
  };

  const supersampleChartStyle: React.CSSProperties = {
    display: 'block',
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  };

  return (
    <div className="dyops__ellipse-frame" style={ellipseStyle}>
      <div className="dyops__scale-frame" style={scaleStyle}>
        <div className={spinClass} style={spinStyle}>
          {ovalSupersample ? (
            <div
              className="dyops__supersample-stack"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: `${innerPx}px`,
                height: `${innerPx}px`,
                marginLeft: `${-innerPx / 2}px`,
                marginTop: `${-innerPx / 2}px`,
                transform: `scale(${downscale})`,
                transformOrigin: 'center center',
              }}
            >
              <PieChart className="dyops__image" style={supersampleChartStyle} lineWidth={20} data={pieData} />
            </div>
          ) : (
            <PieChart className="dyops__image" style={chartStyle} lineWidth={20} data={pieData} />
          )}
        </div>
      </div>
    </div>
  );
};

/** Renders paired spinning Dyop rings (optional oval deformation and axis rotation). */
const Dyops: FC<DyopsProps> = ({
  diametr,
  label,
  active,
  rotation,
  dyops,
  isUserConnected,
  singleRingPreview,
  forceInfantLayout,
  eyeSeparationPx,
}: DyopsProps) => {
  const eyeGapPx = eyeSeparationPx ?? (window.innerWidth <= 1100 ? 65 : 120);
  const normalized = dyops ? withDefaultInfantFields(withDefaultOvalFields(dyops)) : undefined;
  const examMode = normalized?.examMode ?? 'va';
  const ovalMinorRatio = examMode === 'oval' ? clampOvalMinorRatio(normalized?.ovalMinorRatio ?? 1) : 1;
  const ovalAxisDeg = examMode === 'oval' ? normalized?.ovalAxisDeg ?? 0 : 0;
  const isOval = examMode === 'oval' && ovalMinorRatio < 1;

  const pieData = useMemo(() => {
    const primary = normalized?.chromaticPrimary ?? '#000000';
    const secondary = normalized?.chromaticSecondary ?? '#FFFFFF';
    // Live and modal preview both composite on the light ring disk (#909090).
    const { primary: pHex, secondary: sHex } = chromaticHexesForRender(primary, secondary, 'admin');
    const data = [];
    for (let i = 0; i < 16; i++) {
      data.push({ value: 6.25, color: i % 2 === 0 ? pHex : sHex });
    }
    return data;
  }, [normalized?.chromaticPrimary, normalized?.chromaticSecondary]);

  const ellipseStyle: React.CSSProperties = { transform: `rotate(${ovalAxisDeg}deg)` };
  const scaleStyle: React.CSSProperties = { transform: `scale(1, ${ovalMinorRatio})` };

  const ringProps = { diametr, ellipseStyle, scaleStyle, pieData, ovalSupersample: isOval };

  const [setContainerNode, containerSize] = useElementSize();
  const containerWidth = containerSize.width;
  const containerHeight = containerSize.height;
  const patientRingFramePx = getPatientRingFramePx(diametr, containerHeight);
  const infantSide = (normalized?.infantTargetSide ?? normalized?.active) as 'left' | 'right';
  const infantSpinning = !normalized?.pause && normalized?.isStarted;
  const infantRotationSuffix =
    rotation !== undefined ? (rotation ? `-${rotation}` : '') : normalized?.rotation ? '' : '-counter-clockwise';
  const infantSpinClass = `dyops__image-wrapper${infantSpinning ? ` rotating${infantRotationSuffix}` : ''}`;

  if ((normalized?.infantMode || forceInfantLayout) && !singleRingPreview) {
    const layoutProfile = resolveInfantLayoutProfile(label);
    const ringFrameStyle = { minWidth: `${patientRingFramePx}px`, minHeight: `${patientRingFramePx}px` };

    return (
      <div ref={setContainerNode} className="dyops__container dyops__container--infant">
        {getInfantTargetSides().map((side: InfantTargetSide) => {
          const isActiveTarget = infantSide === side;
          // Doctor preview: show the segmented ring before Start; patient keeps empty frames until spin.
          const showRingChart = isActiveTarget && (Boolean(label) || infantSpinning);

          return (
            <div
              key={side}
              className="dyops__infant-target"
              style={{ left: getInfantTargetPosition(layoutProfile, side, patientRingFramePx, containerWidth) }}
            >
              <div
                className={`dyops__item dyops__item--infant-frame${isOval ? ' dyops__item--oval' : ''}`}
                style={ringFrameStyle}
              >
                {showRingChart ? <RingChart {...ringProps} spinClass={infantSpinClass} /> : null}
              </div>
            </div>
          );
        })}
        {label ? (
          <div className="dyops__infant-doctor-indicator">
            <span className={`dyops__infant-doctor-indicator-side${isUserConnected ? ' active' : ''}`}>
              {infantSide === 'left' ? strings.testPage.left : strings.testPage.right}
            </span>
            {infantSpinning && isUserConnected ? (
              <div className={`icon-direction${normalized.rotation ? ' clockwise' : ' counterclockwise'}`} />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (singleRingPreview) {
    return (
      <div className="dyops__container dyops__container--single-preview">
        <div
          className={`dyops__item${isOval ? ' dyops__item--oval' : ''}`}
          style={{ minWidth: `${diametr * 1.5}px`, minHeight: `${diametr * 1.5}px` }}
        >
          <RingChart {...ringProps} spinClass={`dyops__image-wrapper rotating${rotation ? `-${rotation}` : ''}`} />
        </div>
      </div>
    );
  }

  // Patient dual: inner ring edges at 30% / 70% (wider than center-aligned).
  if (!label) {
    const frameStyle = { minWidth: `${patientRingFramePx}px`, minHeight: `${patientRingFramePx}px` };
    const layoutWidth = containerWidth > 0 ? containerWidth : typeof window !== 'undefined' ? window.innerWidth : 0;
    const placement = layoutWidth > 0 ? getPatientDualRingPlacement(layoutWidth, diametr, patientRingFramePx) : null;
    return (
      <div ref={setContainerNode} className="dyops__container dyops__container--patient-separated">
        <div
          className="dyops__item-wrapper dyops__item-wrapper--patient-separated"
          style={
            placement
              ? { left: `${placement.leftInnerPx}px`, transform: 'translate(-100%, -50%)' }
              : { left: '30%', transform: 'translate(-100%, -50%)' }
          }
        >
          <div className={`dyops__item${isOval ? ' dyops__item--oval' : ''}`} style={frameStyle}>
            <RingChart
              {...ringProps}
              spinClass={`dyops__image-wrapper${active === 'left' ? ' rotating' : ''}${
                active === 'left' && rotation ? '-' + rotation : ''
              }`}
            />
          </div>
        </div>
        <div
          className="dyops__item-wrapper dyops__item-wrapper--patient-separated"
          style={
            placement
              ? { left: `${placement.rightInnerPx}px`, transform: 'translate(0, -50%)' }
              : { left: '70%', transform: 'translate(0, -50%)' }
          }
        >
          <div className={`dyops__item${isOval ? ' dyops__item--oval' : ''}`} style={frameStyle}>
            <RingChart
              {...ringProps}
              spinClass={`dyops__image-wrapper${active === 'right' ? ' rotating' : ''}${
                active === 'right' && rotation ? '-' + rotation : ''
              }`}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dyops__container">
      <div className="dyops__item-wrapper">
        <div
          className={`dyops__item${isOval ? ' dyops__item--oval' : ''}`}
          style={{ minWidth: `${diametr * 1.5}px`, minHeight: `${diametr * 1.5}px` }}
        >
          <RingChart
            {...ringProps}
            spinClass={`dyops__image-wrapper${active === 'left' ? ' rotating' : ''}${
              active === 'left' && rotation ? '-' + rotation : ''
            }`}
          />
        </div>
        <div className="dyops-indicator__container">
          <span
            className={`dyops-indicator__text${
              !normalized?.pause && isUserConnected && normalized?.active === 'left' ? ' active' : ''
            }`}
          >
            {strings.testPage.left}
          </span>
          {normalized?.isStarted && !normalized.pause ? (
            <div
              className={`icon-direction${
                normalized?.active === 'left' && isUserConnected
                  ? normalized.rotation
                    ? ' clockwise'
                    : ' counterclockwise'
                  : ' icon-direction--placeholder'
              }`}
              aria-hidden={normalized?.active !== 'left'}
            />
          ) : null}
        </div>
      </div>
      <div className="dyops__item-wrapper" style={{ marginLeft: `${eyeGapPx}px` }}>
        <div
          className={`dyops__item${isOval ? ' dyops__item--oval' : ''}`}
          style={{
            minWidth: `${diametr * 1.5}px`,
            minHeight: `${diametr * 1.5}px`,
          }}
        >
          <RingChart
            {...ringProps}
            spinClass={`dyops__image-wrapper${active === 'right' ? ' rotating' : ''}${
              active === 'right' && rotation ? '-' + rotation : ''
            }`}
          />
        </div>
        <div className="dyops-indicator__container">
          <span
            className={`dyops-indicator__text${
              !normalized?.pause && isUserConnected && normalized?.active === 'right' ? ' active' : ''
            }`}
          >
            {strings.testPage.right}
          </span>
          {normalized?.isStarted && !normalized.pause ? (
            <div
              className={`icon-direction${
                normalized?.active === 'right' && isUserConnected
                  ? normalized.rotation
                    ? ' clockwise'
                    : ' counterclockwise'
                  : ' icon-direction--placeholder'
              }`}
              aria-hidden={normalized?.active !== 'right'}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Dyops;
